// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  QUIZZESTAB.JSX - VERSION: 2026.01.30.1550                                 ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2026.01.30.1550
// ============================================================================
// CHANGELOG:
// 2026.01.30.1550 - Removed wrong first modal after quiz URL direct access
// 2026.01.18.1900 - Improved SOLO/GROUP button animations with alternating glow
// 2026.01.17.1430 - Updated share message for speed selector screen
// 2026.01.17.1430 - Fixed URL to generate slug from name if missing
// 2026.01.16.1700 - FIX: Search now shows child quizzes (like Belfast) as standalone results
// 2026.01.16.1700 - FIX: Search also checks seo_description field
// ============================================================================
// CHANGELOG:
// 2025.12.22.1150 - Fixed pulse buttons to use full vibrant colors (not faded)
// 2025.12.22.1145 - Added gentle pulse animation to SOLO/GROUP buttons when unselected
// 2025.12.22.1130 - Added debug logging for autoOpenGameId
// 2025.12.22.1000 - Share links now use SEO-friendly slug URLs (/quiz/slug)
// 2025.12.22.0955 - Auto-open bottom sheet when arriving via ?game= deep link
// 2025.12.22.0935 - Replaced 🎯 with TRAKtag icon, added Share Quiz button
// 2025.12.18.2130 - NEW: Collapsible parent/child quiz grouping
// 2025.12.18.2100 - PERF: Lazy loading + loading animation for quiz pictures
// 2025.12.18.2100 - REMOVED: Red warning styling and "Missing questions" text
// 2025.12.13.1345 - FIX: Question count now uses String() comparison for game_id type mismatch
// 2025.12.13.1345 - FIX: Search keywords filter checks both snake_case and camelCase
// 2025.12.03.0001 - NEW: Tabbed Solo/Group mode selection panel
// 2025.12.02.1905 - Fixed removeChild DOM error with overlay rendering
// 2025.12.02.1850 - NEW: Bottom sheet pattern for quiz selection (modern UX)
// 2025.12.02.1415 - Red warning for quizzes without pictures/questions
// ============================================================================
import React, { useState, useEffect, useMemo } from 'react';

const QuizzesTab = ({ 
  games = [],
  pictures = [],
  questions = [],
  selectedGameId,
  onSelectGame,
  currentMode,
  onSelectMode,
  onStartPlayerMode,
  onStartGroupMode,
  groupQuizTimer = 0,
  setGroupQuizTimer,
  autoOpenGameId,
  clearAutoOpenGameId
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  
  // 🆕 Tab state: null = no tab selected, 'solo' = solo tab, 'group' = group tab
  const [selectedTab, setSelectedTab] = useState(null);
  
  // 🆕 Remember last used settings from localStorage
  const [selectedSpeed, setSelectedSpeed] = useState(() => {
    return localStorage.getItem('lastSoloSpeed') || 'relaxed';
  });
  
  const [selectedTimer, setSelectedTimer] = useState(() => {
    const saved = localStorage.getItem('lastGroupTimer');
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  
  // 🆕 Save speed to localStorage when changed
  useEffect(() => {
    localStorage.setItem('lastSoloSpeed', selectedSpeed);
  }, [selectedSpeed]);
  
  // 🆕 Save timer to localStorage when changed
  useEffect(() => {
    localStorage.setItem('lastGroupTimer', selectedTimer.toString());
  }, [selectedTimer]);
  
  // 🆕 2025.12.22: Auto-open bottom sheet when arriving via deep link
 useEffect(() => {
  console.log('🔍 QuizzesTab: autoOpenGameId effect running, autoOpenGameId:', autoOpenGameId, 'games.length:', games.length);
  if (autoOpenGameId && games.length > 0) {
    const game = games.find(g => g.game_id === autoOpenGameId);
    if (game) {
      console.log('🔗 Auto-opening quiz:', game.name);
      onSelectGame(autoOpenGameId);
      setSelectedTab('solo');  // <-- FIX: Auto-select solo mode
      setShowBottomSheet(true);
      if (clearAutoOpenGameId) clearAutoOpenGameId();
    }
    }
  }, [autoOpenGameId, games, onSelectGame, clearAutoOpenGameId]);
  
  // Active games only (keep parent quizzes - they show as expandable categories)
  const activeGames = games.filter(g => g.active);
  
  // Filter by search (searches name, description, keywords, AND seo_description)
  const filteredGames = activeGames.filter(g => {
    const search = searchTerm.toLowerCase();
    const nameMatch = g.name?.toLowerCase().includes(search);
    const descMatch = g.description?.toLowerCase().includes(search);
    // 🔧 FIX: Check both snake_case (database) and camelCase field names
    const keywords = g.search_keywords || g.searchKeywords || '';
    const keywordMatch = keywords.toLowerCase().includes(search);
    // 🔧 FIX 2026.01.16: Also search in seo_description field
    const seoDesc = g.seo_description || g.seoDescription || '';
    const seoMatch = seoDesc.toLowerCase().includes(search);
    return nameMatch || descMatch || keywordMatch || seoMatch;
  });

  // 🆕 Track which parent quizzes are expanded
  const [expandedParents, setExpandedParents] = useState(new Set());

  // 🆕 Group quizzes by parent/child relationship
  const groupedQuizzes = useMemo(() => {
    // 🔧 FIX 2026.01.16: When searching, show matching child quizzes as standalone results
    const isSearching = searchTerm.trim().length > 0;
    
    // Find all parent category quizzes (is_parent = true) - these are headers, not playable
    const parentCategories = filteredGames.filter(g => g.is_parent);
    
    // Find all standalone quizzes (no parent_id AND not a parent category)
    // 🔧 FIX: When searching, also include child quizzes that match the search
    const standaloneQuizzes = filteredGames.filter(g => {
      if (isSearching) {
        // When searching, show ALL non-parent quizzes as standalone (including children)
        return !g.is_parent;
      } else {
        // When not searching, only show quizzes without parent_id
        return !g.parent_id && !g.is_parent;
      }
    });
    
    // Find all child quizzes (have parent_id) - only used for grouping when NOT searching
    const children = isSearching ? [] : filteredGames.filter(g => g.parent_id);
    
    // Group children by parent_id
    const childrenByParent = {};
    children.forEach(child => {
      if (!childrenByParent[child.parent_id]) {
        childrenByParent[child.parent_id] = [];
      }
      childrenByParent[child.parent_id].push(child);
    });
    
    // Sort children by display_order
    Object.keys(childrenByParent).forEach(parentId => {
      childrenByParent[parentId].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    });
    
    // Build final list: parent categories with their children, then standalone quizzes
    // 🔧 FIX: When searching, don't show parent categories (they're not playable)
    const parentItems = isSearching ? [] : parentCategories.map(parent => ({
      ...parent,
      children: childrenByParent[parent.game_id] || [],
      childCount: (childrenByParent[parent.game_id] || []).length,
      isParentCategory: true  // Flag to prevent playing
    }));
    
    // Standalone quizzes have no children
    const standaloneItems = standaloneQuizzes.map(quiz => ({
      ...quiz,
      children: [],
      childCount: 0,
      isParentCategory: false
    }));
    
    // Combine: parent categories first (sorted by display_order), then standalone quizzes
    return [
      ...parentItems.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
      ...standaloneItems.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    ];
  }, [filteredGames, searchTerm]);

  // Toggle parent expansion
  const toggleParent = (parentId) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) {
        next.delete(parentId);
      } else {
        next.add(parentId);
      }
      return next;
    });
  };

  // 🆕 Helper to get featured picture for a game
  const getGamePicture = (gameId) => {
    const gamePictures = pictures.filter(p => p.game_id === gameId);
    const featured = gamePictures.find(p => p.featuredImage || p.featured_image);
    const firstActive = gamePictures.find(p => p.active !== false);
    const pic = featured || firstActive || gamePictures[0];
    return pic?.image_url || pic?.file_path || pic?.url || pic?.imageUrl || pic?.src || null;
  };

  // 🆕 Truncate description
  const truncateDesc = (text, maxLen = 60) => {
    if (!text) return 'Test your knowledge!';
    return text.length > maxLen ? text.substring(0, maxLen) + '...' : text;
  };

  // Speed modes for Solo play
  const speedModes = [
    { id: 'relaxed', name: 'Scenic', icon: '🚶', timer: 'No timer', multiplier: '1x', color: 'from-green-500 to-emerald-600' },
    { id: 'thinker', name: 'Local', icon: '🚌', timer: '15 sec', multiplier: '1.25x', color: 'from-blue-500 to-cyan-600' },
    { id: 'speed', name: 'Express', icon: '🚄', timer: '10 sec', multiplier: '1.5x', color: 'from-orange-500 to-amber-600' },
    { id: 'lightning', name: 'Bullet', icon: '🚀', timer: '6 sec', multiplier: '2x', color: 'from-red-500 to-rose-600' }
  ];

  // Group Quiz timer options
  const groupTimerOptions = [
    { value: 0, label: 'NONE', color: 'from-gray-500 to-gray-600' },
    { value: 10, label: '10s', color: 'from-green-500 to-emerald-600' },
    { value: 15, label: '15s', color: 'from-blue-500 to-cyan-600' },
    { value: 25, label: '25s', color: 'from-orange-500 to-amber-600' },
    { value: 35, label: '35s', color: 'from-red-500 to-rose-600' }
  ];

  const selectedGame = games.find(g => g.game_id === selectedGameId);

  // Handle quiz selection - show bottom sheet
  const handleSelectQuiz = (gameId) => {
    onSelectGame(gameId);
    setSelectedTab(null); // Reset tab selection when opening panel
    setShowBottomSheet(true);
  };

  // 🆕 Handle Solo Game start
  const handleStartSoloQuiz = () => {
    // Set the mode based on selected speed
    onSelectMode(selectedSpeed);
    onStartPlayerMode();
    setShowBottomSheet(false);
  };

  // Handle Group Quiz start
  const handleStartGroupQuiz = () => {
    if (setGroupQuizTimer) {
      setGroupQuizTimer(selectedTimer);
    }
    onStartGroupMode();
    setShowBottomSheet(false);
  };

  // 🆕 Handle speed selection
  const handleSpeedSelect = (speedId) => {
    setSelectedSpeed(speedId);
    onSelectMode(speedId);
  };

  // Close bottom sheet
  const handleCloseSheet = () => {
    setShowBottomSheet(false);
    setSelectedTab(null);
    setShareCopied(false);
  };

  // 🆕 2025.12.22: Share quiz link using slug URL
  const handleShareQuiz = (game) => {
    // Generate slug from name if missing
    let gameSlug = game.slug || '';
    if (!gameSlug && game.name) {
      gameSlug = game.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }
    const quizUrl = gameSlug 
      ? `https://traktag.com/quiz/${gameSlug}`
      : `https://traktag.com`;
    const shareText = `Hello, I have just discovered the ${game.name} - I am just starting my game and I thought of you as its just up your street. Join me and try it at ${quizUrl}`;
    
    if (navigator.share) {
      navigator.share({
        title: `TRAKtag - ${game.name}`,
        text: shareText,
        url: quizUrl
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareText);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 pb-20 relative">
      <div className="max-w-md mx-auto px-4 py-4">
        
        {/* Header */}
        <h1 className="text-white text-2xl font-bold mb-4 flex items-center gap-2">
          <img src="/favicon-48.png" alt="" className="w-7 h-7" /> Select Quiz
        </h1>

        {/* Search Box */}
        <div className="relative mb-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search quizzes..."
            className="w-full bg-gray-800 text-white px-4 py-3 pl-10 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              ✕
            </button>
          )}
        </div>

        {/* Quiz List - Full height scrollable */}
        <div className={`space-y-3 overflow-y-auto transition-all duration-300 ${showBottomSheet ? 'max-h-48 opacity-50' : 'max-h-[calc(100vh-200px)]'}`}>
          {groupedQuizzes.length > 0 ? groupedQuizzes.map(game => {
            const picUrl = getGamePicture(game.game_id);
            const isSelected = selectedGameId === game.game_id;
            const hasChildren = game.childCount > 0;
            const isExpanded = expandedParents.has(game.game_id);
            const isParentCategory = game.isParentCategory || game.is_parent;
            
            return (
              <div key={game.game_id}>
                {/* Parent Quiz Button */}
                <button
                  onClick={() => (hasChildren || isParentCategory) ? toggleParent(game.game_id) : handleSelectQuiz(game.game_id)}
                  className={`w-full text-left bg-gray-800 rounded-xl p-3 border-2 transition-all ${
                    isSelected && showBottomSheet
                      ? 'border-yellow-500 bg-gray-750 scale-[0.98]'
                      : 'border-gray-700 hover:border-blue-500 hover:bg-gray-750'
                  }`}
                >
                  <div className="flex gap-3 items-center">
                    {/* Quiz Picture */}
                    <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-gray-700 animate-pulse">
                      {picUrl ? (
                        <img 
                          src={picUrl} 
                          alt={game.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onLoad={(e) => e.target.parentElement.classList.remove('animate-pulse')}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.parentElement.classList.remove('animate-pulse');
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-teal-600">
                          <img src="/favicon-48.png" alt="" className="w-8 h-8 opacity-80" />
                        </div>
                      )}
                    </div>
                    
                    {/* Quiz Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold text-base leading-tight">
                        {isParentCategory && '📂 '}{game.name}
                      </h3>
                      <p className="text-gray-400 text-xs mt-0.5 leading-tight">
                        {(hasChildren || isParentCategory)
                          ? `${game.childCount} quiz${game.childCount !== 1 ? 'zes' : ''} available`
                          : truncateDesc(game.description, 50)
                        }
                      </p>
                    </div>
                    
                    {/* Arrow or Expand Icon */}
                    <div className="text-gray-500 text-xl">
                      {(hasChildren || isParentCategory) ? (isExpanded ? '▼' : '▶') : '›'}
                    </div>
                  </div>
                </button>

                {/* Child Quizzes (Sections) - shown when expanded */}
                {(hasChildren || isParentCategory) && isExpanded && (
                  <div className="ml-4 mt-2 space-y-2 border-l-2 border-blue-500/30 pl-3">
                    {game.children.map(child => {
                      const childPicUrl = getGamePicture(child.game_id);
                      const isChildSelected = selectedGameId === child.game_id;
                      
                      return (
                        <button
                          key={child.game_id}
                          onClick={() => handleSelectQuiz(child.game_id)}
                          className={`w-full text-left bg-gray-800/80 rounded-lg p-2.5 border-2 transition-all ${
                            isChildSelected && showBottomSheet
                              ? 'border-yellow-500 bg-gray-750 scale-[0.98]'
                              : 'border-gray-700/50 hover:border-blue-500 hover:bg-gray-750'
                          }`}
                        >
                          <div className="flex gap-2.5 items-center">
                            {/* Small Quiz Picture */}
                            <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-gray-700 animate-pulse">
                              {childPicUrl ? (
                                <img 
                                  src={childPicUrl} 
                                  alt={child.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                  onLoad={(e) => e.target.parentElement.classList.remove('animate-pulse')}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                    e.target.parentElement.classList.remove('animate-pulse');
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-teal-500">
                                  <span className="text-sm">📝</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Child Quiz Info */}
                            <div className="flex-1 min-w-0">
                              <h4 className="text-white font-medium text-sm leading-tight">
                                {child.name}
                              </h4>
                              <p className="text-gray-400 text-xs leading-tight">
                                {truncateDesc(child.description, 40)}
                              </p>
                            </div>
                            
                            {/* Arrow */}
                            <div className="text-gray-500 text-lg">›</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }) : (
            <p className="text-gray-400 text-center py-8">
              {searchTerm ? 'No quizzes match your search' : 'No quizzes available'}
            </p>
          )}
        </div>

      </div>

      {/* Bottom Sheet Overlay - Always rendered, visibility controlled */}
      <div 
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          showBottomSheet ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleCloseSheet}
      />

      {/* Bottom Sheet */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-out ${
        showBottomSheet ? 'translate-y-0' : 'translate-y-full'
      }`}>
        <div className="max-w-md mx-auto bg-gray-800 rounded-t-3xl border-t-2 border-blue-500 shadow-2xl">
          
          {/* Drag Handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div 
              className="w-12 h-1.5 bg-gray-600 rounded-full cursor-pointer"
              onClick={handleCloseSheet}
            />
          </div>

          {selectedGame && showBottomSheet && (
            <div className="px-5 pb-6">
              
              {/* Selected Quiz Header */}
              <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-700">
                {/* Quiz Picture */}
                <div className="w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden bg-gray-700">
                  {getGamePicture(selectedGame.game_id) ? (
                    <img 
                      src={getGamePicture(selectedGame.game_id)} 
                      alt={selectedGame.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-2xl">
                      <img src="/favicon-48.png" alt="" className="w-10 h-10 opacity-80" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`text-xs font-medium ${selectedTab === 'group' ? 'text-green-400' : 'text-blue-400'}`}>
                    {selectedTab === 'group' ? 'Group Quiz' : 'Selected Quiz'}
                  </p>
                  <h2 className="text-white font-bold text-xl">{selectedGame.name}</h2>
                </div>
                <button 
                  onClick={handleCloseSheet}
                  className="text-gray-400 hover:text-white text-2xl p-2"
                >
                  ✕
                </button>
              </div>
              
              {/* 🆕 Share Quiz Button */}
              <button
                onClick={() => handleShareQuiz(selectedGame)}
                className="w-full mb-4 py-2 px-4 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white text-sm font-medium flex items-center justify-center gap-2 transition-all"
              >
                {shareCopied ? (
                  <>✅ Link Copied!</>
                ) : (
                  <>📤 Share this Quiz with Friends</>
                )}
              </button>

              {/* 🆕 TABS - Solo / Group */}
              {/* 🔧 FIX 2026.01.18: Alternating glow animation for unselected state */}
              <style>{`
                @keyframes subtleGlow {
                  0%, 100% { box-shadow: 0 0 5px rgba(34, 197, 94, 0.4), 0 0 10px rgba(34, 197, 94, 0.2); }
                  50% { box-shadow: 0 0 15px rgba(34, 197, 94, 0.8), 0 0 25px rgba(34, 197, 94, 0.4); }
                }
                .glow-solo { animation: subtleGlow 2s ease-in-out infinite; }
                .glow-group { animation: subtleGlow 2s ease-in-out infinite 1s; }
              `}</style>
              <div className="flex mb-4 bg-gray-700 rounded-xl p-1">
                <button
                  onClick={() => setSelectedTab('solo')}
                  className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    selectedTab === 'solo'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                      : selectedTab === null
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg glow-solo'
                        : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <span>🎮</span> SOLO
                </button>
                <button
                  onClick={() => setSelectedTab('group')}
                  className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    selectedTab === 'group'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                      : selectedTab === null
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg glow-group'
                        : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <span>👥</span> GROUP
                </button>
              </div>

              {/* Content based on selected tab */}
              {selectedTab === null ? (
                /* No tab selected - show prompt */
                <div className="text-center py-8">
                  <p className="text-gray-400 text-lg">👆 Select a game mode</p>
                </div>
              ) : selectedTab === 'solo' ? (
                /* SOLO MODE CONTENT */
                <>
                  {/* Speed Mode Selection */}
                  <div className="mb-4">
                    <p className="text-white font-bold text-sm mb-2">🚂 Select Speed:</p>
                    <div className="grid grid-cols-4 gap-2">
                      {speedModes.map(mode => (
                        <button
                          key={mode.id}
                          onClick={() => handleSpeedSelect(mode.id)}
                          className={`p-2 rounded-xl text-center transition-all ${
                            selectedSpeed === mode.id
                              ? `bg-gradient-to-r ${mode.color} text-white ring-2 ring-yellow-400`
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          <div className="text-lg">{mode.icon}</div>
                          <p className="font-bold text-xs">{mode.name}</p>
                          <p className="text-[10px] opacity-75">{mode.multiplier}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Start Solo Game Button */}
                  <button
                    onClick={handleStartSoloQuiz}
                    className="w-full py-4 px-6 rounded-xl font-black text-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-400 hover:to-emerald-500 transition-all shadow-lg"
                  >
                    🎮 START SOLO GAME
                  </button>
                </>
              ) : (
                /* GROUP MODE CONTENT */
                <>
                  {/* Timer Selection */}
                  <div className="mb-4">
                    <p className="text-white font-bold text-sm mb-2">⏱️ Countdown Timer:</p>
                    <div className="grid grid-cols-5 gap-2">
                      {groupTimerOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => setSelectedTimer(option.value)}
                          className={`py-2 px-1 rounded-lg text-center transition-all ${
                            selectedTimer === option.value
                              ? `bg-gradient-to-r ${option.color} text-white ring-2 ring-yellow-400`
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          <p className="font-bold text-sm">{option.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Start Group Quiz Button */}
                  <button
                    onClick={handleStartGroupQuiz}
                    className="w-full py-4 px-6 rounded-xl font-black text-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-400 hover:to-emerald-500 transition-all shadow-lg"
                  >
                    👥 START GROUP QUIZ
                  </button>
                </>
              )}

              {/* Back to Search Button */}
              <button
                onClick={handleCloseSheet}
                className="w-full mt-3 py-2 text-blue-400 hover:text-blue-300 text-sm font-medium"
              >
                ← Back to Quiz List
              </button>

            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default QuizzesTab;
